import { useEffect, useState, useCallback } from 'react'
import { useBoolean, useInterval } from 'usehooks-ts'
import { useVisibilityChange } from './use-visibility-change'
import { useEventById } from './use-event-by-id'

export function useCounter(initialValue) {
  const [count, setCount] = useState(initialValue ?? 0)

  const increment = useCallback(() => {
    setCount((x) => x + 1)
  }, [])

  const decrement = useCallback(() => {
    setCount((x) => x - 1)
  }, [])

  const reset = useCallback(() => {
    setCount(initialValue ?? 0)
  }, [initialValue])

  useEffect(() => {
    setCount(initialValue ?? 0)
  }, [initialValue])

  return {
    count,
    increment,
    decrement,
    reset,
    setCount,
  }
}

export function useCountdown({
  countStart,
  countStop = 0,
  intervalMs = 1000,
  isIncrement = false,
}) {
  const { count, increment, decrement, reset: resetCounter } = useCounter(countStart)

  /*
   * Note: used to control the useInterval
   * running: If true, the interval is running
   * start: Should set running true to trigger interval
   * stop: Should set running false to remove interval.
   */
  const {
    value: isCountdownRunning,
    setTrue: startCountdown,
    setFalse: stopCountdown,
  } = useBoolean(false)

  // Will set running false and reset the seconds to initial value.
  const resetCountdown = useCallback(() => {
    stopCountdown()
    resetCounter()
  }, [stopCountdown, resetCounter])

  const countdownCallback = useCallback(() => {
    if (count === countStop) {
      stopCountdown()
      return
    }

    if (isIncrement) {
      increment()
    } else {
      decrement()
    }
  }, [count, countStop, decrement, increment, isIncrement, stopCountdown])

  useInterval(countdownCallback, isCountdownRunning ? intervalMs : null)

  return [count, { startCountdown, stopCountdown, resetCountdown }]
}

// Helper function to calculate seconds until a target date
export function getSecondsUntilEventStarts(targetDate) {
  if (!targetDate) return 0

  const now = Date.now()
  const targetTime = new Date(targetDate).getTime()
  const timeDifference = targetTime - now

  return Math.max(0, Math.floor(timeDifference / 1000))
}

export function useEventCountdown(finishAt) {
  // Calculate initial seconds until target date
  const { isVisible } = useVisibilityChange()
  const { refetch } = useEventById()

  // Use the library's useCountdown
  const [count, controllers] = useCountdown({
    countStart: finishAt,
    countStop: 0,
    intervalMs: 1000,
    isIncrement: false,
  })

  // Auto-start countdown if there's a valid date
  useEffect(() => {
    if (finishAt && finishAt > 0) {
      controllers.startCountdown()
    }
  }, [finishAt])

  // Resync countdown when visibility changes (device wakes up from suspension)
  useEffect(() => {
    if (isVisible) {
      refetch()
    }
  }, [isVisible])

  return {
    secondsRemaining: count,
    isActive: !!finishAt && count > 0,
    hasReached: !!finishAt && count === 0,
  }
}

// Utilidad para formatear el tiempo restante
export function formatCountdown(seconds) {
  const days = Math.floor(seconds / (24 * 60 * 60))
  const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60))
  const minutes = Math.floor((seconds % (60 * 60)) / 60)
  const remainingSeconds = seconds % 60

  const parts = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)
  parts.push(`${remainingSeconds}s`)

  return {
    days,
    hours,
    minutes,
    seconds: remainingSeconds,
    formatted: parts.join(' '),
  }
}

export function WithCountdown({ onStart, onFinish, finishAt, children }) {
  const { isActive, hasReached } = useEventCountdown(finishAt)
  useEffect(() => {
    if (isActive && onStart) {
      onStart()
    }
  }, [isActive])
  useEffect(() => {
    if (hasReached && onFinish) {
      onFinish()
    }
  }, [hasReached])
  return children
}
